import { useState, useEffect, useRef, useCallback } from 'react'

import { Drawer, Divider, Select, Text } from '@mantine/core'

import { GET_APPLICANTS } from 'src/graphql/applicants.graphql'

import BulkActionDrawerHost from './BulkActionDrawer'
import { applicantsBulkActions } from './bulkActionRegistry'
import {
  type BulkActionName,
  type BulkActionResult,
  type SelectedApplicant,
} from './bulkActiontypes'
import ApplicantList from './components/ApplicantList'

type BulkActionsDrawerWrapperProps = {
  opened: boolean
  onClose: () => void
  selectedApplicants: SelectedApplicant[]
  setSelectedApplicants: (next: SelectedApplicant[]) => void
  onSelectAction: (actionName: BulkActionName | null) => void
  activeAction: BulkActionName | null
}

const BulkActionsDrawerWrapper = ({
  opened,
  onClose,
  selectedApplicants,
  setSelectedApplicants,
  onSelectAction,
  activeAction,
}: BulkActionsDrawerWrapperProps) => {
  const selectedApplicantsCount = selectedApplicants.length
  const [actionLoading, setActionLoading] = useState(false)
  const [actionResults, setActionResults] = useState<BulkActionResult[] | null>(
    null
  )
  // Tracks whether any action run had at least one success while the drawer
  // was open, so the selection can be cleared when the drawer closes. Kept in
  // a ref (not derived from actionResults) because actionResults is reset
  // before the selection should be cleared.
  const actionSucceededRef = useRef(false)

  const handleSetResults = useCallback(
    (results: BulkActionResult[] | null) => {
      if (results) {
        actionSucceededRef.current = results.some((result) => result.success)
      }
      setActionResults(results)
    },
    []
  )

  const clearSelectionIfActionSucceeded = useCallback(() => {
    if (actionSucceededRef.current) {
      actionSucceededRef.current = false
      setSelectedApplicants([])
    }
  }, [setSelectedApplicants])

  // Reset per-action state when the action changes. The selection is kept so
  // the user can run another bulk action on the same applicants; it is only
  // cleared when the drawer closes.
  useEffect(() => {
    setActionLoading(false)
    setActionResults(null)
  }, [activeAction])

  const handleClose = () => {
    clearSelectionIfActionSucceeded()
    // Reset action selection on close
    onSelectAction(null)
    onClose()
  }

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      title="Bulk Actions"
      classNames={{
        title: 'text-base font-semibold text-doubleNickel-gray-900',
        content: 'flex flex-col h-full',
        body: 'flex flex-col flex-1 px-4 pb-4 overflow-hidden',
      }}
      position="right"
      closeOnClickOutside={!actionLoading}
      closeOnEscape={!actionLoading}
    >
      <div className="flex h-full flex-col gap-4 overflow-auto">
        {selectedApplicantsCount === 0 ? (
          <div className="flex flex-col gap-1">
            <Text size="sm" fw={500} className="text-doubleNickel-gray-900">
              No applicants selected
            </Text>
            <Text size="xs" className="text-doubleNickel-gray-500">
              Select one or more applicants from the table to run a bulk action.
            </Text>
          </div>
        ) : (
          <>
            <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
              {`${selectedApplicantsCount} ${
                selectedApplicantsCount === 1 ? 'APPLICANT' : 'APPLICANTS'
              }`}
            </Text>

            <ApplicantList
              applicants={selectedApplicants}
              setApplicants={setSelectedApplicants}
              bulkSmsLoading={actionLoading}
              bulkSmsResult={actionResults}
            />

            <Divider variant="dashed" />

            <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
              BULK ACTIONS
            </Text>
            <div className="flex flex-row items-center justify-between">
              <Text size="xs">Bulk Action Type</Text>
              <div className="w-[250px]">
                <Select
                  placeholder="Choose an action"
                  disabled={actionLoading}
                  value={activeAction}
                  data={applicantsBulkActions.map((action) => ({
                    value: action.id,
                    label: action.label,
                  }))}
                  onChange={(value) => {
                    onSelectAction(value as BulkActionName)
                  }}
                  clearable
                  checkIconPosition="right"
                  size="xs"
                  classNames={{
                    input: 'text-xs text-right text-doubleNickel-gray-900',
                    dropdown: 'border-doubleNickel-gray-200',
                    option:
                      'text-xs text-doubleNickel-gray-900 data-[combobox-selected]:bg-transparent data-[combobox-active]:bg-transparent hover:bg-doubleNickel-gray-100',
                  }}
                />
              </div>
            </div>

            <BulkActionDrawerHost
              activeActionId={activeAction}
              selectedApplicants={selectedApplicants}
              setSelectedApplicants={setSelectedApplicants}
              onClose={() => onSelectAction(null)}
              refetchQueries={[GET_APPLICANTS]}
              setLoading={setActionLoading}
              setResults={handleSetResults}
            />
          </>
        )}
      </div>
    </Drawer>
  )
}

export default BulkActionsDrawerWrapper
